import React, { useState, useEffect } from 'react';
import { 
    Table, Card, Button, Modal, Form,
    Select, Tooltip, Space, Tag, Badge,
    Typography, Spin, message
} from 'antd';
import { 
    PlusOutlined, EditOutlined, DeleteOutlined, 
    LinkOutlined, CloudServerOutlined,
    GlobalOutlined, KeyOutlined, ExclamationCircleOutlined,
    ShopOutlined, ApiOutlined, NotificationOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { 
    addSellerAccount, 
    updateSellerAccount, 
    deleteSellerAccount,
    fetchWorkers,
    updateWorkerSpApiCredentials,
    createSqsDestination,
    deleteSqsDestination,
    updateSqsSubscriptions,
    checkSqsDestinationStatus,
    checkSqsDestinationExists
} from '../actions';

// Import extracted modal components
import EditSellerAccountModal from './modals/EditSellerAccountModal';
import SpApiConfigurationModal from './modals/SpApiConfigurationModal';
import SqsDestinationModal from './modals/SqsDestinationModal';

const { Text } = Typography;
const { confirm } = Modal;

const SellerAccountSettings = ({ sellerAccounts = [], loading }) => {
    const dispatch = useDispatch();
    const [editingAccount, setEditingAccount] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isSpApiModalVisible, setIsSpApiModalVisible] = useState(false);
    const [isSqsModalVisible, setIsSqsModalVisible] = useState(false);
    const [selectedWorker, setSelectedWorker] = useState(null);
    const [workerSettingsLoading, setWorkerSettingsLoading] = useState(false);
    
    // Get workers list from redux store
    const { workers } = useSelector(state => state.adminSettings);
    
    // Fetch workers when component mounts
    useEffect(() => {
        dispatch(fetchWorkers());
    }, [dispatch]);

    // Ensure sellerAccounts is always an array
    const accountsArray = Array.isArray(sellerAccounts) ? sellerAccounts : [];

    const handleAdd = () => {
        setEditingAccount(null);
        setIsModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingAccount(record);
        setIsModalVisible(true);
    };

    const handleSave = async (values) => {
        try {
            const action = editingAccount 
                ? updateSellerAccount(editingAccount.id, values) 
                : addSellerAccount(values);
                
            dispatch(action);
            setIsModalVisible(false);
        } catch (error) {
            console.error('Error saving seller account:', error);
            message.error('Không thể lưu tài khoản seller');
        }
    };

    const handleDelete = (record) => {
        confirm({
            title: 'Xác nhận xóa tài khoản',
            icon: <ExclamationCircleOutlined />,
            content: `Bạn có chắc chắn muốn xóa tài khoản "${record.name}" không? Hành động này không thể hoàn tác.`,
            okText: 'Xóa',
            okType: 'danger',
            cancelText: 'Hủy',
            onOk() {
                dispatch(deleteSellerAccount(record.id));
            }
        });
    };

    const handleSelectWorker = (account) => {
        setEditingAccount(account);
        setSelectedWorker(null);
        setIsSpApiModalVisible(true);
    };

    const handleSetupSpApi = (account, worker) => {
        setEditingAccount(account);
        setSelectedWorker(worker);
        setIsSpApiModalVisible(true);
    };

    const handleSaveSpApi = async (values) => {
        try {
            setWorkerSettingsLoading(true);
            
            // Build the credentials payload
            const credentialsPayload = {
                workerId: values.workerId,
                clientId: values.clientId,
                clientSecret: values.clientSecret,
                refreshToken: values.refreshToken,
                spApiClientId: values.spApiClientId,
                spApiClientSecret: values.spApiClientSecret
            };
            
            // Dispatch action to save SP API credentials and link worker to seller account
            const result = await dispatch(updateWorkerSpApiCredentials(editingAccount.id, credentialsPayload));
            
            if (result.success) {
                // Show success message
                message.success('Cấu hình SP API thành công');
                setIsSpApiModalVisible(false);
            } else {
                // Show error message
                message.error(result.error || 'Không thể cấu hình SP API');
            }
        } catch (error) {
            console.error('Error saving SP API configuration:', error);
            message.error('Không thể cấu hình SP API');
        } finally {
            setWorkerSettingsLoading(false);
        }
    };

    const handleSetupSqsDestination = async (account) => {
        // Đặt account hiện tại và mở modal trước
        setEditingAccount(account);
        setIsSqsModalVisible(true);
        
        // Chỉ cần kiểm tra và đồng bộ nếu account chưa có thông tin notificationDestination hoặc cần verify
        if (!account.notificationDestination || !account.notificationDestination.destinationId) {
            // Sau đó mới bắt đầu quá trình kiểm tra với Amazon
            setWorkerSettingsLoading(true);
            
            try {
                // Kiểm tra xem SQS destination có tồn tại thực tế trên Amazon không
                const result = await dispatch(checkSqsDestinationExists(account.id));
                
                console.log('SQS destination check result:', result);
                
                let updatedAccount = { ...account };
                
                if (result.success && result.exists && result.verified) {
                    // Nếu destination tồn tại và đã được xác minh với Amazon
                    if (result.data && result.data.notificationDestination) {
                        // Sử dụng dữ liệu notificationDestination đã được xử lý từ API
                        updatedAccount = {
                            ...account,
                            notificationDestination: result.data.notificationDestination
                        };
                        
                        console.log('Updated account with destination data:', updatedAccount.notificationDestination);
                    }
                    
                    // Hiển thị thông báo
                    message.info('SQS destination đã được xác thực và đồng bộ với Amazon');
                } else if (result.success && !result.exists) {
                    // Destination không tồn tại trên Amazon
                    updatedAccount = {
                        ...account,
                        notificationDestination: null
                    };
                    
                    message.warning('Không tìm thấy SQS destination trên Amazon');
                } else {
                    // Không thể kết nối với worker hoặc Amazon
                    message.warning('Không thể xác thực destination với Amazon. Vui lòng kiểm tra kết nối worker.');
                }
                
                // Cập nhật account sau khi kiểm tra xong
                setEditingAccount(updatedAccount);
            } catch (error) {
                console.error('Error checking SQS destination:', error);
                message.error('Có lỗi khi kiểm tra SQS destination');
            } finally {
                setWorkerSettingsLoading(false);
            }
        }
        // Nếu đã có thông tin notificationDestination trong database, không cần gọi API
    };

    const handleSaveSqsDestination = async (values) => {
        try {
            setWorkerSettingsLoading(true);
            
            // Determine which operation to perform based on the updateType parameter
            if (values.updateType === 'deleteDestination') {
                // Delete the SQS destination
                const result = await dispatch(deleteSqsDestination({
                    sellerId: values.sellerId,
                    destinationId: values.destinationId
                }));
                
                if (result.success) {
                    message.success('SQS destination deleted successfully');
                    
                    // Update the current editing account to remove notification destination
                    const updatedAccount = {
                        ...editingAccount,
                        notificationDestination: null
                    };
                    setEditingAccount(updatedAccount);
                    
                    setIsSqsModalVisible(false);
                } else {
                    message.error(result.error || 'Failed to delete SQS destination');
                }
            } else if (values.updateType === 'updateSubscriptions') {
                // Update subscriptions for existing destination
                const result = await dispatch(updateSqsSubscriptions({
                    sellerId: values.sellerId,
                    destinationId: values.destinationId,
                    eventTypes: values.eventTypes
                }));
                
                if (result.success) {
                    message.success('SQS subscriptions updated successfully');
                    
                    // Update the current editing account with new subscription info
                    if (result.data && result.data.notificationDestination) {
                        const updatedAccount = {
                            ...editingAccount,
                            notificationDestination: result.data.notificationDestination
                        };
                        setEditingAccount(updatedAccount);
                    }
                    
                    setIsSqsModalVisible(false);
                } else {
                    message.error(result.error || 'Failed to update SQS subscriptions');
                }
            } else if (values.updateType === 'checkStatus') {
                // Check status of the destination
                const result = await dispatch(checkSqsDestinationStatus(
                    values.sellerId,
                    values.destinationId
                ));
                
                if (result.success) {
                    message.success('SQS destination status checked successfully');
                    
                    // Update the current editing account with latest status info
                    if (result.data && result.data.notificationDestination) {
                        const updatedAccount = {
                            ...editingAccount,
                            notificationDestination: result.data.notificationDestination
                        };
                        setEditingAccount(updatedAccount);
                    }
                    
                    // Don't close modal, just show the status
                } else {
                    message.error(result.error || 'Failed to check SQS destination status');
                }
            } else {
                // Create new destination (default operation)
                const result = await dispatch(createSqsDestination(values));
                
                if (result.success) {
                    message.success('SQS destination configured successfully');
                    
                    // Update the current editing account with new destination info
                    if (result.data && result.data.notificationDestination) {
                        const updatedAccount = {
                            ...editingAccount,
                            notificationDestination: result.data.notificationDestination
                        };
                        setEditingAccount(updatedAccount);
                    }
                    
                    setIsSqsModalVisible(false);
                } else {
                    message.error(result.error || 'Failed to configure SQS destination');
                }
            }
        } catch (error) {
            console.error('Error handling SQS destination:', error);
            message.error('Could not configure SQS destination');
        } finally {
            setWorkerSettingsLoading(false);
        }
    };

    // Helper function to extract region from SQS ARN
    const extractRegionFromArn = (arn) => {
        if (!arn || typeof arn !== 'string') return '';
        
        // ARN format: arn:aws:sqs:region:account-id:queue-name
        const arnParts = arn.split(':');
        if (arnParts.length >= 4 && arnParts[0] === 'arn' && arnParts[1] === 'aws' && arnParts[2] === 'sqs') {
            return arnParts[3]; // Return the region part
        }
        
        return '';
    };

    const getStatusBadge = (status) => {
        let color = 'default';
        let text = 'Không xác định';
        
        switch(status) {
            case 'active':
                color = 'success';
                text = 'Hoạt động';
                break;
            case 'inactive':
                color = 'default';
                text = 'Không hoạt động';
                break;
            case 'suspended':
                color = 'error';
                text = 'Tạm ngưng';
                break;
        }
        
        return <Badge status={color} text={text} />;
    };

    const getTypeTag = (type) => {
        switch (type) {
            case 'sync':
                return <Tag color="green">Đồng bộ dữ liệu</Tag>;
            case 'queue':
                return <Tag color="purple">Xử lý task</Tag>;
            default:
                return <Tag>Không xác định</Tag>;
        }
    };

    const columns = [
        {
            title: 'Tên tài khoản',
            dataIndex: 'name',
            key: 'name',
            width: 200,
            render: (text, record) => (
                <Space>
                    <ShopOutlined style={{ color: '#1890ff' }} />
                    <Text strong>{text}</Text>
                </Space>
            )
        },
        {
            title: 'Seller ID',
            dataIndex: 'sellerId',
            key: 'sellerId',
            width: 150,
            render: text => <code>{text}</code>
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            width: 200,
        },
        {
            title: 'Marketplaces',
            dataIndex: 'marketplaces',
            key: 'marketplaces',
            width: 140,
            render: markets => (
                <>
                    {(markets || []).map(market => (
                        <Tag key={market} color="blue" style={{ marginBottom: '4px' }}>
                            {market}
                        </Tag>
                    ))}
                </>
            )
        },
        {
            title: 'Webhook URL',
            dataIndex: 'webhookUrl',
            key: 'webhookUrl',
            ellipsis: true,
            render: url => (
                <Tooltip title={url}>
                    <a
                        color={url ? 'processing' : 'default'} 
                        className="webhook-url-tag"
                    >
                        {url ? <span><GlobalOutlined /> {url}</span> : 'Chưa cấu hình'}
                    </a>
                </Tooltip>
            )
        },
        // {
        //     title: 'Notifications',
        //     key: 'notifications',
        //     width: 180,
        //     render: (_, record) => {
        //         // Check if there's a notification destination set up
        //         const hasSqsDestination = record.notificationDestination?.destinationId;
                
        //         if (hasSqsDestination) {
        //             return (
        //                 <Space>
        //                     <Tooltip title={`SQS: ${record.notificationDestination.sqsQueueUrl}`}>
        //                         <Tag color="success" icon={<NotificationOutlined />}>
        //                             SQS Configured
        //                         </Tag>
        //                     </Tooltip>
        //                     <Button 
        //                         size="small" 
        //                         type="text"
        //                         icon={<EditOutlined />}
        //                         onClick={() => handleSetupSqsDestination(record)}
        //                         title="Chỉnh sửa SQS destination"
        //                     />
        //                 </Space>
        //             );
        //         }
                
        //         if (record.worker) {
        //             return (
        //                 <Button 
        //                     size="small" 
        //                     type="dashed"
        //                     icon={<NotificationOutlined />}
        //                     onClick={() => handleSetupSqsDestination(record)}
        //                 >
        //                     Setup SQS
        //                 </Button>
        //             );
        //         }
                
        //         return (
        //             <Tooltip title="Worker required for SQS setup">
        //                 <Tag color="default">No Notifications</Tag>
        //             </Tooltip>
        //         );
        //     }
        // },
        {
            title: 'Worker',
            dataIndex: 'worker',
            key: 'worker',
            width: 320,
            render: (worker, record) => {
                if (worker) {
                    return (
                        <Space>
                            <CloudServerOutlined style={{ color: '#52c41a' }} />
                            <Text>{worker.name}</Text>
                            <Tag color={worker.status === 'online' ? 'success' : 'error'}>
                                {worker.status === 'online' ? 'Online' : 'Offline'}
                            </Tag>
                            <Button 
                                type="link" 
                                size="small" 
                                icon={<KeyOutlined />}
                                onClick={() => handleSetupSpApi(record, worker)}
                                title="Cấu hình SP API"
                            />
                        </Space>
                    );
                }
                
                return (
                    <Button 
                        type="dashed" 
                        size="small" 
                        icon={<CloudServerOutlined />}
                        onClick={() => handleSelectWorker(record)}
                    >
                        Gán worker
                    </Button>
                );
            }
        },
        {
            title: 'Trạng thái',
            dataIndex: 'status',
            key: 'status',
            width: 150,
            render: status => getStatusBadge(status)
        },
        {
            title: 'Thao tác',
            key: 'action',
            width: 100,
            fixed: 'right',
            render: (_, record) => (
                <Space>
                    <Button 
                        type="text" 
                        icon={<EditOutlined />} 
                        onClick={() => handleEdit(record)}
                    />
                    <Button 
                        type="text" 
                        danger 
                        icon={<DeleteOutlined />} 
                        onClick={() => handleDelete(record)}
                    />
                </Space>
            ),
        },
    ];

    return (
        <div className="seller-account-settings">
            <Card 
                title="Quản lý tài khoản Amazon Seller" 
                extra={
                    <Button 
                        type="primary" 
                        icon={<PlusOutlined />} 
                        onClick={handleAdd}
                    >
                        Thêm mới
                    </Button>
                } 
                bordered={false}
            >
                <Table 
                    columns={columns} 
                    dataSource={accountsArray.map(account => ({...account, key: account.id}))} 
                    loading={loading}
                    className="seller-account-table"
                    pagination={{ pageSize: 5 }}
                />
            </Card>

            {/* Use the extracted EditSellerAccountModal component */}
            <EditSellerAccountModal
                visible={isModalVisible}
                editingAccount={editingAccount}
                onCancel={() => setIsModalVisible(false)}
                onSave={handleSave}
                loading={loading}
            />

            {/* Use the extracted SpApiConfigurationModal component */}
            <SpApiConfigurationModal
                visible={isSpApiModalVisible}
                editingAccount={editingAccount}
                selectedWorker={selectedWorker}
                loading={workerSettingsLoading}
                workers={workers}
                onCancel={() => setIsSpApiModalVisible(false)}
                onSave={handleSaveSpApi}
            />
            
            {/* Add the SQS Destination modal */}
            <SqsDestinationModal
                visible={isSqsModalVisible}
                editingAccount={editingAccount}
                loading={workerSettingsLoading}
                onCancel={() => setIsSqsModalVisible(false)}
                onSave={handleSaveSqsDestination}
            />
        </div>
    );
};

export default SellerAccountSettings;